import React, { useEffect, useState } from 'react';
import { ArrowLeft, ArrowUpRight, Eye, UserPlus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const GREEN = '#097049';
const DARK_GREEN = '#0A3F2B';
const CREAM = '#F6F1EA';
const INK = '#172033';
const MUTED = '#6B7280';
const OFFICIAL_WA = '6285212546871';
const WA_LINK = `https://wa.me/${OFFICIAL_WA}?text=${encodeURIComponent('Halo Stockara, saya ingin coba gratis 14 hari dan aktivasi akun bisnis.')}`;

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterInfo, setShowRegisterInfo] = useState(window.location.pathname === '/register');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleError = params.get('error');
    if (googleError === 'google_not_registered') {
      setError('Akun Google ini belum terdaftar di Stockara. Minta owner/admin mendaftarkan email kamu terlebih dahulu.');
    }
    if (googleError === 'google_inactive') {
      setError('Akun Google ini sedang nonaktif. Hubungi owner/admin bisnis kamu.');
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const loginData = await login(form.email, form.password);
      if (!loginData?.user?.email_verified) {
        navigate('/verify-email', { state: { email: form.email, mustChangePassword: loginData?.user?.must_change_password } });
      } else if (loginData?.user?.must_change_password) {
        navigate('/first-login');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Email atau password belum sesuai. Coba cek lagi ya.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stockara-login-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box}
        body{margin:0}
        .stockara-login-page{
          position:relative;
          overflow:hidden;
          min-height:100vh;
          --sl-display-font:"Instrument Serif", Georgia, "Times New Roman", serif;
          --sl-body-font:"Plus Jakarta Sans", Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-family:var(--sl-body-font);
          color:${INK};
          background:
            linear-gradient(rgba(9,112,73,.075) 1px, transparent 1px),
            linear-gradient(90deg, rgba(9,112,73,.075) 1px, transparent 1px),
            #fbfaf7;
          background-size:28px 28px,28px 28px,auto;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          padding:32px 18px;
        }
        .stockara-login-page::before{
          content:'';
          position:absolute;
          inset:-24%;
          z-index:0;
          pointer-events:none;
          background:
            radial-gradient(circle at 16% 18%, rgba(22,199,132,.24), transparent 30%),
            radial-gradient(circle at 82% 72%, rgba(9,112,73,.16), transparent 28%),
            radial-gradient(circle at 50% 50%, rgba(246,241,234,.86), transparent 36%);
          filter:blur(1px);
          animation:sl-auth-gradient-drift 22s ease-in-out infinite alternate;
        }
        @keyframes sl-auth-gradient-drift{
          0%{transform:translate3d(-2%, -1%, 0) scale(1)}
          50%{transform:translate3d(2%, 2%, 0) scale(1.04)}
          100%{transform:translate3d(-1%, 3%, 0) scale(1.02)}
        }
        .sl-login-shell{
          position:relative;
          z-index:1;
          width:min(100%, 980px);
          min-height:640px;
          display:grid;
          grid-template-columns:minmax(0,.93fr) minmax(0,1.07fr);
          gap:42px;
          padding:14px;
          border:1px solid rgba(15,23,42,.10);
          border-radius:22px;
          background:rgba(255,255,255,.88);
          box-shadow:0 26px 80px rgba(15,23,42,.12);
          backdrop-filter:blur(14px);
        }
        .sl-login-brand{
          position:relative;
          overflow:hidden;
          min-height:612px;
          border-radius:16px;
          padding:42px;
          display:flex;
          flex-direction:column;
          justify-content:flex-end;
          background:
            linear-gradient(rgba(9,112,73,.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(9,112,73,.07) 1px, transparent 1px),
            ${CREAM};
          background-size:18px 18px,18px 18px,auto;
        }
        .sl-login-brand::before{
          content:'';
          position:absolute;
          inset:-24%;
          pointer-events:none;
          background:
            radial-gradient(circle at 78% 16%, rgba(42,238,141,.44), transparent 34%),
            radial-gradient(circle at 22% 72%, rgba(9,112,73,.16), transparent 28%),
            radial-gradient(circle at 52% 42%, rgba(255,250,244,.70), transparent 36%);
          animation:sl-auth-gradient-drift 18s ease-in-out infinite alternate;
        }
        .sl-login-brand > *{
          position:relative;
          z-index:1;
        }
        .sl-brand-logo{
          display:flex;
          align-items:center;
          gap:10px;
          color:${GREEN};
          font-weight:900;
          font-size:22px;
          margin-bottom:28px;
        }
        .sl-brand-logo img{width:32px;height:32px;border-radius:9px;object-fit:contain}
        .sl-login-brand h1{
          width:min(100%, 430px);
          margin:0;
          color:#202020;
          font-family:var(--sl-display-font);
          font-size:clamp(40px,4.8vw,58px);
          line-height:0.98;
          letter-spacing:0;
          font-weight:400;
        }
        .sl-login-brand h1 span{color:${GREEN};text-decoration:underline;text-decoration-thickness:4px;text-underline-offset:7px}
        .sl-login-form-panel{
          display:flex;
          align-items:center;
          justify-content:center;
          padding:42px 30px;
        }
        .sl-login-form-inner{width:min(100%, 440px)}
        .sl-back-home{
          display:inline-flex;
          align-items:center;
          gap:12px;
          min-height:38px;
          color:${MUTED};
          text-decoration:none;
          font-size:14px;
          font-weight:700;
          margin-bottom:64px;
        }
        .sl-back-home span:first-child{
          width:28px;height:28px;border:1px solid rgba(15,23,42,.12);border-radius:10px;
          display:flex;align-items:center;justify-content:center;background:#fff;color:#64748b;
        }
        .sl-login-title{margin:0;color:${INK};font-family:var(--sl-display-font);font-size:32px;line-height:1.06;font-weight:400;letter-spacing:0}
        .sl-login-desc{margin:10px 0 58px;color:${MUTED};font-size:15px;line-height:1.42;max-width:420px}
        .sl-login-label{display:block;margin-bottom:8px;color:#222;font-size:14px;font-weight:650}
        .sl-login-field{margin-bottom:18px}
        .sl-login-input-wrap{position:relative}
        .sl-login-input{
          width:100%;height:42px;border:1px solid #d7dbe0;border-radius:11px;background:#fff;
          padding:0 14px;color:${INK};font:inherit;font-size:14px;outline:none;
          transition:border-color .16s ease, box-shadow .16s ease;
        }
        .sl-login-input:focus{border-color:${GREEN};box-shadow:0 0 0 3px rgba(9,112,73,.12)}
        .sl-password-toggle{
          position:absolute;right:10px;top:50%;transform:translateY(-50%);
          border:0;background:transparent;color:#9aa0a6;display:flex;cursor:pointer;padding:5px;
        }
        .sl-label-row{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:8px}
        .sl-forgot{color:${GREEN};font-size:13px;font-weight:750;text-decoration:underline;text-underline-offset:3px}
        .sl-error{margin:0 0 16px;padding:11px 13px;border-radius:11px;border:1px solid #fecaca;background:#fef2f2;color:#b91c1c;font-size:13px;font-weight:650}
        .sl-login-submit{
          width:100%;height:42px;border:0;border-radius:11px;background:linear-gradient(180deg,#1b8a5f,#074a31);
          color:#fff;font:inherit;font-size:14px;font-weight:850;cursor:pointer;
          box-shadow:0 14px 28px rgba(9,112,73,.18);transition:opacity .16s ease, transform .16s ease;
        }
        .sl-login-submit:hover{transform:translateY(-1px)}
        .sl-login-submit:disabled{opacity:.65;cursor:not-allowed;transform:none}
        .sl-divider{display:grid;grid-template-columns:1fr auto 1fr;gap:14px;align-items:center;margin:20px 0}
        .sl-divider::before,.sl-divider::after{content:'';height:1px;background:#e2e5e8}
        .sl-divider span{font-size:13px;color:${MUTED}}
        .sl-register-btn{
          width:100%;height:42px;border:1px solid #d7dbe0;border-radius:11px;background:#fff;color:#202020;
          font:inherit;font-size:14px;font-weight:760;cursor:pointer;transition:border-color .16s ease, color .16s ease, background .16s ease;
        }
        .sl-register-btn:hover{border-color:${GREEN};color:${GREEN};background:#f7fbf8}
        .sl-google-btn{
          width:100%;height:42px;border:1px solid #d7dbe0;border-radius:11px;background:#fff;color:#202020;
          font:inherit;font-size:14px;font-weight:760;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;
          transition:border-color .16s ease, color .16s ease, background .16s ease;
        }
        .sl-google-btn:hover{border-color:${GREEN};color:${GREEN};background:#f7fbf8}
        .sl-login-terms{position:relative;z-index:1;margin-top:88px;text-align:center;color:#6b7280;font-size:13px;line-height:1.45}
        .sl-login-terms a{color:#2f3a44;text-decoration:underline;text-underline-offset:3px}
        .sl-modal-backdrop{
          position:fixed;inset:0;z-index:50;display:flex;align-items:center;justify-content:center;padding:20px;
          background:rgba(248,250,248,.60);backdrop-filter:blur(12px);
        }
        .sl-register-modal{
          width:min(100%, 520px);border-radius:24px;background:#fff;border:1px solid rgba(15,23,42,.10);
          box-shadow:0 30px 90px rgba(15,23,42,.18);overflow:hidden;
        }
        .sl-modal-head{height:58px;display:flex;align-items:center;justify-content:space-between;padding:0 18px;border-bottom:1px solid #eef0f2}
        .sl-modal-title{display:flex;align-items:center;gap:10px;color:${INK};font-size:17px;font-weight:850}
        .sl-modal-title svg{color:${GREEN}}
        .sl-modal-close{width:32px;height:32px;border:0;border-radius:999px;background:#f1f4f3;color:#64748b;display:flex;align-items:center;justify-content:center;cursor:pointer}
        .sl-modal-body{padding:28px 36px 34px}
        .sl-modal-body h2{margin:0 0 18px;color:${INK};font-family:var(--sl-display-font);font-size:26px;line-height:1.12;font-weight:400}
        .sl-register-info{border-radius:18px;background:#f8faf9;padding:18px 20px}
        .sl-register-option{padding:0 0 18px;border-bottom:1px solid #e5e8ea}
        .sl-register-option + .sl-register-option{padding:18px 0 0;border-bottom:0}
        .sl-register-option strong{display:block;margin-bottom:8px;color:${GREEN};font-size:14px}
        .sl-register-option.is-no strong{color:#c2410c}
        .sl-register-option p{margin:0;color:#374151;font-size:14px;line-height:1.5}
        .sl-whatsapp-cta{
          margin-top:14px;width:100%;height:42px;border:1px solid #d7dbe0;border-radius:14px;background:#fff;
          display:flex;align-items:center;justify-content:center;gap:8px;color:${INK};text-decoration:none;font-weight:850;font-size:14px;
          transition:border-color .16s ease, background .16s ease, color .16s ease;
        }
        .sl-whatsapp-cta:hover{border-color:${GREEN};background:#eefaf4;color:${GREEN}}
        @media(max-width:820px){
          .stockara-login-page{justify-content:flex-start;padding:14px}
          .sl-login-shell{grid-template-columns:1fr;gap:0;min-height:auto;padding:10px}
          .sl-login-brand{min-height:260px;padding:28px}
          .sl-login-brand h1{font-size:34px}
          .sl-login-form-panel{padding:28px 16px}
          .sl-back-home{margin-bottom:34px}
          .sl-login-desc{margin-bottom:34px}
          .sl-login-terms{margin-top:28px}
        }
        @media(max-width:460px){
          .sl-login-brand{display:none}
          .sl-login-shell{border-radius:18px}
          .sl-modal-body{padding:24px 18px 26px}
        }
        @media(prefers-reduced-motion:reduce){
          .stockara-login-page::before,
          .sl-login-brand::before{animation:none}
        }
      `}</style>

      <div className="sl-login-shell">
        <aside className="sl-login-brand">
          <div>
            <div className="sl-brand-logo">
              <img src="/stockara-ai-icon.png" alt="" />
              <span>Stockara AI</span>
            </div>
            <h1>Platform bisnis untuk <span>AI CS</span>, stok, kasir, dan laporan.</h1>
          </div>
        </aside>

        <section className="sl-login-form-panel">
          <div className="sl-login-form-inner">
            <a href="/" className="sl-back-home">
              <span><ArrowLeft size={15} /></span>
              <span>Back to home</span>
            </a>

            <h2 className="sl-login-title">Log in to your existing account</h2>
            <p className="sl-login-desc">
              Masuk dengan email yang sudah terdaftar sebagai member di akun bisnis dengan langganan aktif.
            </p>

            {error && <p className="sl-error">{error}</p>}

            <form onSubmit={handleSubmit}>
              <div className="sl-login-field">
                <label className="sl-login-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  className="sl-login-input"
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                  placeholder="Masukkan email"
                  required
                />
              </div>

              <div className="sl-login-field">
                <div className="sl-label-row">
                  <label className="sl-login-label" htmlFor="password" style={{ marginBottom: 0 }}>Password</label>
                  <a className="sl-forgot" href="/forgot-password">Lupa Password?</a>
                </div>
                <div className="sl-login-input-wrap">
                  <input
                    id="password"
                    className="sl-login-input"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(event) => setForm({ ...form, password: event.target.value })}
                    placeholder="Masukkan password"
                    required
                  />
                  <button type="button" className="sl-password-toggle" onClick={() => setShowPassword((value) => !value)} aria-label="Tampilkan password">
                    <Eye size={17} />
                  </button>
                </div>
              </div>

              <button className="sl-login-submit" type="submit" disabled={loading}>
                {loading ? 'Memproses...' : 'Masuk'}
              </button>
            </form>

            <div className="sl-divider"><span>atau</span></div>

            <button className="sl-google-btn" type="button" onClick={() => { window.location.href = 'https://stockara.my.id/api/auth/google'; }}>
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Masuk dengan Google
            </button>

            <div className="sl-divider"><span>atau</span></div>

            <button className="sl-register-btn" type="button" onClick={() => setShowRegisterInfo(true)}>
              Daftar
            </button>
          </div>
        </section>
      </div>

      <p className="sl-login-terms">
        Dengan menggunakan Stockara, Anda menyetujui<br />
        <a href="/terms">Syarat & Ketentuan</a> dan <a href="/privacy">Kebijakan Privasi</a>
      </p>

      {showRegisterInfo && (
        <div className="sl-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="register-title">
          <div className="sl-register-modal">
            <div className="sl-modal-head">
              <div className="sl-modal-title" id="register-title">
                <UserPlus size={19} />
                <span>Daftar Akun Baru</span>
              </div>
              <button className="sl-modal-close" type="button" onClick={() => setShowRegisterInfo(false)} aria-label="Tutup">
                <X size={18} />
              </button>
            </div>
            <div className="sl-modal-body">
              <h2>Apakah bisnis kamu sudah punya langganan Stockara yang aktif?</h2>
              <div className="sl-register-info">
                <div className="sl-register-option">
                  <strong>Ya</strong>
                  <p>Minta owner atau admin bisnis menambahkan email kamu sebagai anggota di dashboard Stockara.</p>
                </div>
                <div className="sl-register-option is-no">
                  <strong>Belum</strong>
                  <p>Hubungi tim Stockara untuk aktivasi trial dan setup akun bisnis terlebih dahulu.</p>
                  <a className="sl-whatsapp-cta" href={WA_LINK} target="_blank" rel="noopener noreferrer">
                    Hubungi Stockara
                    <ArrowUpRight size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
